import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { FiltersButton } from "./FiltersButton";
import { SearchBar } from "./SearchBar";
import { SortSelector } from "./SortSelector";
import { FiltersPanel } from "./FiltersPanel";
import { ActiveFilters } from "./Activefilters";
import { useAuth } from "../../contexts/AuthContext";
import { useFilters } from "../../contexts/FilterContext";

const BANNER_DISMISSED_KEY = "igbanwo:locationBannerDismissed";

/**
 * Soft banner shown when:
 *   - the user is logged in
 *   - they have no saved location
 *   - they haven't dismissed the banner this session
 *
 * Encourages them to set a location for nearby filtering.
 */
const NoLocationBanner = ({ onDismiss }) => {
  return (
    <div className="mb-4 flex items-start gap-3 p-4 bg-igbanwo-mint/10 border border-igbanwo-mint/20 rounded-lg">
      <MapPin className="w-5 h-5 text-igbanwo-mint flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium">
          Set your location to see trades near you
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Add your location in your profile to filter listings by distance.{" "}
          <Link
            to="/edit-profile"
            className="text-igbanwo-mint hover:underline font-medium"
          >
            Set location now
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-muted-foreground hover:text-foreground flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const BrowseFilterSection = ({ onFiltersChange }) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { isAuthenticated, currentUser } = useAuth();
  const { filters } = useFilters();

  // Banner dismissal persists for the session (sessionStorage), not forever —
  // user gets reminded next time they visit.
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(BANNER_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const dismissBanner = () => {
    try {
      sessionStorage.setItem(BANNER_DISMISSED_KEY, "1");
    } catch {
      // sessionStorage might be unavailable in private mode — fail silent
    }
    setBannerDismissed(true);
  };

  // Show the banner only if:
  //   - logged in
  //   - no saved location
  //   - haven't dismissed this session
  //   - haven't already manually filtered by location for this session
  const userHasLocation =
    currentUser?.location?.coordinates?.length === 2;
  const showLocationBanner =
    isAuthenticated &&
    !userHasLocation &&
    !bannerDismissed &&
    !filters.useLocationFilter;

  const toggleFilters = () => {
    setIsFiltersOpen((prev) => !prev);
  };

  return (
    <div className="mb-8">
      {showLocationBanner && <NoLocationBanner onDismiss={dismissBanner} />}

      <div className="flex flex-col md:flex-row gap-4">
        <SearchBar />

        <div className="flex gap-3">
          <SortSelector />
          <FiltersButton isOpen={isFiltersOpen} onClick={toggleFilters} />
        </div>
      </div>

      {isFiltersOpen && <FiltersPanel />}

      <ActiveFilters />
    </div>
  );
};